import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Search, X, BookOpen, Code, Music, Dumbbell, MapPin, User, TrendingUp } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useTutors } from '@/hooks/useTutors';
import { useSubjects } from '@/hooks/useSubjects';

interface SearchModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const SearchModal = ({ isOpen, onClose }: SearchModalProps) => {
  const navigate = useNavigate();
  const { tutors, searchTutors } = useTutors();
  const { subjects } = useSubjects();
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [isSearching, setIsSearching] = useState(false);

  const focusSubjects = [
    { name: 'English', icon: BookOpen, href: '/subjects/english', color: 'text-blue-600 bg-blue-50' },
    { name: 'Programming', icon: Code, href: '/subjects/programming', color: 'text-purple-600 bg-purple-50' },
    { name: 'Music', icon: Music, href: '/subjects/music', color: 'text-pink-600 bg-pink-50' },
    { name: 'Fitness', icon: Dumbbell, href: '/subjects/fitness', color: 'text-green-600 bg-green-50' },
    { name: 'Travel', icon: MapPin, href: '/subjects/travel', color: 'text-teal-600 bg-teal-50' },
  ];

  const popularSearches = [
    'Math Tutor',
    'English Speaking',
    'Guitar Lessons',
    'Python Programming',
    'Yoga Instructor',
  ];

  useEffect(() => {
    if (searchQuery.trim().length > 0) {
      handleSearch();
    } else {
      setSearchResults([]);
    }
  }, [searchQuery]);

  const handleSearch = async () => {
    setIsSearching(true);
    try {
      const results = await searchTutors({
        searchQuery,
        selectedSubject: '',
        selectedCountry: '',
        priceRange: '',
        availability: ''
      });
      setSearchResults(results.slice(0, 6)); // Limit to 6 results
    } catch (error) {
      console.error('Search error:', error);
    } finally {
      setIsSearching(false);
    }
  };

  const handleSubjectClick = (href: string) => {
    navigate(href);
    onClose();
    setSearchQuery('');
  };

  const handleTutorClick = (tutorId: string) => {
    navigate(`/tutor/${tutorId}`);
    onClose();
    setSearchQuery('');
  };

  const handleViewAllResults = () => {
    navigate(`/tutors?search=${encodeURIComponent(searchQuery)}`);
    onClose();
    setSearchQuery('');
  };

  const handlePopularSearchClick = (search: string) => {
    setSearchQuery(search);
  };

  const getTutorName = (tutor: any) => {
    if (tutor.name && tutor.name.trim()) {
      return tutor.name;
    }
    if (tutor.expand?.user?.name) {
      return tutor.expand.user.name;
    }
    return 'Unknown Tutor';
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] p-0 gap-0">
        <DialogHeader className="p-4 pb-3 border-b">
          <DialogTitle className="text-lg font-semibold">Search Tutors & Subjects</DialogTitle>
        </DialogHeader>

        {/* Search Input */}
        <div className="px-4 py-3 border-b">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              type="text"
              placeholder="Search for tutors, subjects, or topics..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 pr-10 h-11"
              autoFocus
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 hover:bg-gray-100 rounded-full p-1"
              >
                <X className="h-4 w-4 text-gray-400" />
              </button>
            )}
          </div>
        </div>

        <ScrollArea className="max-h-[calc(90vh-180px)]">
          <div className="p-4">
            {/* Search Results */}
            {searchQuery.trim().length > 0 ? (
              <div>
                {isSearching ? (
                  <div className="flex items-center justify-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                  </div>
                ) : searchResults.length > 0 ? (
                  <div>
                    <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center">
                      <User className="h-4 w-4 mr-2" />
                      Tutors
                    </h3>
                    <div className="space-y-2 mb-4">
                      {searchResults.map((tutor) => (
                        <button
                          key={tutor.id}
                          onClick={() => handleTutorClick(tutor.id)}
                          className="w-full text-left p-3 hover:bg-gray-50 rounded-lg transition-colors border border-gray-100"
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-gray-900 truncate">{getTutorName(tutor)}</p>
                              <div className="flex flex-wrap gap-1 mt-1">
                                {tutor.subjects?.slice(0, 3).map((subject: string, idx: number) => (
                                  <Badge key={idx} variant="secondary" className="text-xs">
                                    {subject}
                                  </Badge>
                                ))}
                              </div>
                            </div>
                            <div className="text-sm font-medium text-blue-600 ml-2">
                              View Profile
                            </div>
                          </div>
                        </button>
                      ))}
                    </div>
                    <Button
                      variant="outline"
                      className="w-full"
                      onClick={handleViewAllResults}
                    >
                      View All Results
                    </Button>
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <p className="text-gray-500">No tutors found for "{searchQuery}"</p>
                    <p className="text-sm text-gray-400 mt-2">Try searching for a different subject or topic</p>
                  </div>
                )}
              </div>
            ) : (
              <>
                {/* Focus Subjects */}
                <div className="mb-6">
                  <h3 className="text-sm font-semibold text-gray-900 mb-3">Focus Subjects</h3>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                    {focusSubjects.map((subject) => (
                      <button
                        key={subject.name}
                        onClick={() => handleSubjectClick(subject.href)}
                        className={`${subject.color} p-3 rounded-lg hover:shadow-md transition-all text-left`}
                      >
                        <subject.icon className="h-5 w-5 mb-1" />
                        <p className="font-medium text-sm">{subject.name}</p>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Popular Searches */}
                <div>
                  <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center">
                    <TrendingUp className="h-4 w-4 mr-2" />
                    Popular Searches
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {popularSearches.map((search, idx) => (
                      <Button
                        key={idx}
                        variant="outline"
                        size="sm"
                        onClick={() => handlePopularSearchClick(search)}
                        className="text-sm"
                      >
                        {search}
                      </Button>
                    ))}
                  </div>
                </div>

                {/* Quick Links */}
                <div className="mt-6 pt-4 border-t">
                  <div className="grid grid-cols-2 gap-2">
                    <Button
                      variant="ghost"
                      onClick={() => {
                        navigate('/tutors');
                        onClose();
                      }}
                      className="justify-start"
                    >
                      <User className="h-4 w-4 mr-2" />
                      Browse All Tutors
                    </Button>
                    <Button
                      variant="ghost"
                      onClick={() => {
                        navigate('/subjects');
                        onClose();
                      }}
                      className="justify-start"
                    >
                      <BookOpen className="h-4 w-4 mr-2" />
                      All Subjects
                    </Button>
                  </div>
                </div>
              </>
            )}
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
};

export default SearchModal;
